/**
 * HA-588 fire-time stock outcomes.
 *
 * Two things the terminal has to say that it did not before: a line that could not send because it
 * would take an ingredient below zero (the rest of the order did send), and — when a line that was
 * already sent is removed — whether the food came back, because only the operator knows.
 */
import type { OrderItem } from "../types";

/** One order line the server refused on stock grounds. The rest of the order sent. */
export interface RejectedStockLine {
	orderItemId: string;
	stockItemId: string;
	stockItemName: string;
	/** The server's copy, shown verbatim — it is the same sentence everywhere the refusal appears. */
	message: string;
}

/** Which lines did not send, so the order view can flag exactly those and no others. */
export const rejectedItemIds = (rejections: RejectedStockLine[]): string[] =>
	rejections.map((r) => r.orderItemId);

export const isRejected = (
	rejections: RejectedStockLine[],
	itemId: string,
): boolean => rejections.some((r) => r.orderItemId === itemId);

/**
 * The success toast is suppressed whenever anything was refused, for the same reason HA-619
 * suppresses it on a print warning: the operator must not be told it went and told it didn't in
 * the same breath. Some of the order did go — the refusal message says so itself.
 *
 * Deliberately not named shouldShowSendSuccess: HA-619 already owns that name for print warnings,
 * and the send toast needs BOTH to be clear.
 */
export const nothingWasRefused = (rejections: RejectedStockLine[]): boolean =>
	rejections.length === 0;

/**
 * Whether removing this line has to ask "Returned to stock?".
 *
 * Only a line that was SENT ever deducted anything, so only a sent line has something to give
 * back. Asking about an unsent one would be a question with no consequence, and the operator
 * learns to dismiss the prompt — which is how the one that matters gets dismissed too.
 */
export const needsReturnPrompt = (
	item: Pick<OrderItem, "sentToKitchen">,
): boolean => item.sentToKitchen === true;

export const RETURN_PROMPT_TITLE = "Returned to stock?";

export const RETURN_PROMPT_BODY =
	"This item was already sent to the kitchen. If the food came back unused, say Yes and the stock goes back on. If it was made and thrown away, say No.";

/**
 * The id every stock movement from one send is keyed on.
 *
 * Derived from what is being sent rather than generated, so it survives the case it exists for: a
 * till that loses the network mid-send and retries builds the same id from the same order and the
 * same lines, and the server deducts once. A generated id would be new on every attempt, which is
 * exactly the double deduction the key is meant to prevent.
 *
 * A genuinely different send cannot collide with it: sending consumes the lines it sends, so the
 * same set is never legitimately sent twice, and a line removed and re-added comes back with a new
 * id. Sorted because the order the screen happens to hold the lines in is not part of the intent.
 */
export function sendIdFor(orderId: string, itemIds: string[]): string {
	return `${orderId}:${[...itemIds].sort().join(",")}`;
}

/**
 * The lines a send will carry, or null when they cannot be known.
 *
 * Null rather than an empty array, and the caller refuses on it: an empty list is
 * indistinguishable from "this order has nothing unsent", and the two need opposite handling. The
 * send id and the set the server deducts must come from THIS one list — deriving the id here and
 * leaving the server to resolve the set itself computes the same answer twice from different state
 * at different moments, which is how a retry deducts a second time.
 */
export function linesToSend(
	explicit: string[] | undefined,
	order:
		{ items: { id?: string; sentToKitchen: boolean }[] } | undefined | null,
): string[] | null {
	if (explicit) return explicit;
	if (!order) return null;

	return order.items
		.filter((item) => !item.sentToKitchen && item.id)
		.map((item) => item.id!);
}
